import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  TextInput,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { useUserSelection } from '../../../contexts/UserSelectionContext';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { loyaltyPointService } from '../../../services/loyaltyPointService';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('LoyaltyPointDashboard');

type LoyaltyPointData = {
  id: string;
  category: string;
  loyaltyPointType: string;
  companyName: string;
  accountNumber: string;
  username: string;
  password: string;
  websiteUrl: string;
  notes: string;
};

type CategoryData = {
  id: string;
  name: string;
  icon: any;
  count: number;
  color: string;
  loyaltyPoints: LoyaltyPointData[];
};

const categories: CategoryData[] = [
  { id: 'all', name: 'All', icon: require('../../../assets/icons/loyalty.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
  { id: 'retail-shopping', name: 'Retail & Shopping', icon: require('../../../assets/icons/shopping.png'), count: 0, color: '#29BFC0', loyaltyPoints: [] },
  { id: 'travel-hospitality', name: 'Travel & Hospitality', icon: require('../../../assets/icons/travel.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
  { id: 'food-beverage', name: 'Food & Beverage', icon: require('../../../assets/icons/food.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
  { id: 'credit-cards-financial', name: 'Credit Cards & Financial', icon: require('../../../assets/icons/credit-card.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
  { id: 'health-wellness', name: 'Health & Wellness', icon: require('../../../assets/icons/health.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
  { id: 'other', name: 'Other', icon: require('../../../assets/icons/loyalty.png'), count: 0, color: '#FFFFFF', loyaltyPoints: [] },
];

type CategoryCardProps = {
  item: CategoryData;
  isSelected: boolean;
  onPress: () => void;
  navigation: RootStackNavigationProp;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, onPress, navigation }) => {
  return (
    <TouchableOpacity
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#29BFC0' : '#FFFFFF' }
      ]}
      onPress={onPress}
    >
      <View style={styles.categoryCardContent}>
        <View style={styles.categoryIconContainer}>
          <Image source={item.icon} style={styles.categoryIcon} />
        </View>
        <Text style={[
          styles.categoryName,
          { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
        ]}>{item.name}</Text>
        <Text style={[
          styles.categoryCount,
          { color: isSelected ? '#FFFFFF' : '#6B7280' }
        ]}>{item.count} {item.count === 1 ? 'Program' : 'Programs'}</Text>
      </View>
      <TouchableOpacity
        style={[
          styles.addButton,
          { backgroundColor: isSelected ? '#FFFFFF' : '#29BFC0' }
        ]}
        onPress={() => navigation.navigate('Loyalty', {
          screen: 'LoyaltyPointInputScreen',
          params: { category: item.id }
        } as any)}
      >
        <Image
          source={require('../../../assets/icons/add.png')}
          style={[
            styles.addIcon,
            { tintColor: isSelected ? '#29BFC0' : '#FFFFFF' }
          ]}
        />
      </TouchableOpacity>
    </TouchableOpacity>
  );
};

type LoyaltyPointItemProps = {
  item: LoyaltyPointData;
  onPress: () => void;
};

const LoyaltyPointItem: React.FC<LoyaltyPointItemProps> = ({ item, onPress }) => {
  const getIcon = (category: string) => {
    const categoryLower = category.toLowerCase();
    switch (categoryLower) {
      case 'retail-shopping':
        return require('../../../assets/icons/shopping.png');
      case 'travel-hospitality':
        return require('../../../assets/icons/travel.png');
      case 'food-beverage':
        return require('../../../assets/icons/food.png');
      case 'credit-cards-financial':
        return require('../../../assets/icons/credit-card.png');
      case 'health-wellness':
        return require('../../../assets/icons/health.png');
      case 'other':
      default:
        return require('../../../assets/icons/loyalty.png');
    }
  };

  return (
    <TouchableOpacity style={styles.loyaltyPointItem} onPress={onPress}>
      <View style={styles.loyaltyPointIcon}>
        <Image source={getIcon(item.category)} style={styles.loyaltyPointIconImage} />
      </View>
      <View style={styles.loyaltyPointInfo}>
        <Text style={styles.loyaltyPointName}>{item.companyName}</Text>
        <Text style={styles.loyaltyPointUrl}>{item.loyaltyPointType}</Text>
      </View>
      <View style={styles.loyaltyPointType}>
        {item.accountNumber && <Text style={styles.loyaltyPointAccountTop}>{item.accountNumber}</Text>}
        <TouchableOpacity style={styles.moreButton}>
          <Text style={styles.moreButtonText}>•••</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

export const LoyaltyPointDashboardScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [, setAllLoyaltyPoints] = useState<LoyaltyPointData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState<CategoryData[]>(categories);
  const [searchText, setSearchText] = useState('');

  // Handle route params for pre-selecting category
  useEffect(() => {
    const params = route.params as any;
    if (params?.selectedCategory) {
      setSelectedCategory(params.selectedCategory);
    }
  }, [route.params]);

  // K5: another person's loyalty logins are theirs and only after a
  // verified death; before that, nothing.
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId } = useUserSelection();
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;

  useFocusEffect(
    useCallback(() => {
      loadLoyaltyPoints();
    }, [selectedUserId, isViewingOthersData, isDeathVerifiedForSelectedUser])
  );

  const loadLoyaltyPoints = async () => {
    setLoading(true);
    try {
      const loyaltyPoints = requiresDeathVerification
        ? []
        : await loyaltyPointService.getAllLoyaltyPoints(isViewingOthersData ? selectedUserId : undefined);
      logger.debug('Loaded loyalty points:', loyaltyPoints);
      logger.debug('Loyalty point categories:', loyaltyPoints.map(p => p.category));
      setAllLoyaltyPoints(loyaltyPoints);
      
      // Update category data with loyalty point counts
      const updatedCategories = categories.map(category => {
        if (category.id === 'all') {
          return {
            ...category,
            count: loyaltyPoints.length,
            loyaltyPoints: loyaltyPoints,
          };
        } else {
          const categoryLoyaltyPoints = loyaltyPoints.filter(p => p.category.toLowerCase() === category.id.toLowerCase());
          logger.debug(`Category ${category.id}: ${categoryLoyaltyPoints.length} loyalty points`);
          return {
            ...category,
            count: categoryLoyaltyPoints.length,
            loyaltyPoints: categoryLoyaltyPoints,
          };
        }
      });
      
      setCategoryData(updatedCategories);
    } catch (error) {
      logger.error('Error loading loyalty points:', error);
      Alert.alert('Error', 'Failed to load loyalty points');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleLoyaltyPointPress = (loyaltyPoint: LoyaltyPointData) => {
    navigation.navigate('Loyalty', { 
      screen: 'LoyaltyPointDataScreen', 
      params: { loyaltyPoint } 
    } as any);
  };

  const handleAddLoyaltyPoint = () => {
    navigation.navigate('Loyalty', { screen: 'LoyaltyPointInputScreen' } as any);
  };

  const selectedCategoryData = categoryData.find(cat => cat.id === selectedCategory);
  const displayLoyaltyPoints = selectedCategoryData?.loyaltyPoints || [];

  const filteredLoyaltyPoints = displayLoyaltyPoints.filter(loyaltyPoint =>
    searchText === '' ||
    loyaltyPoint.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
    loyaltyPoint.loyaltyPointType.toLowerCase().includes(searchText.toLowerCase()) ||
    loyaltyPoint.category.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading loyalty points...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder="Search loyalty points"
            placeholderTextColor="#9CA3AF"
            value={searchText}
            onChangeText={setSearchText}
          />
          <Image
            source={require('../../../assets/icons/search.png')}
            style={styles.searchIcon}
          />
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>Manage</Text>
          <Text style={styles.allLoyaltyPointsText}>All Loyalty Points</Text>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoriesList}
          contentContainerStyle={{ paddingRight: 30 }}
        >
          {categoryData.map((category) => (
            <CategoryCard
              key={category.id}
              item={category}
              isSelected={selectedCategory === category.id}
              onPress={() => handleCategorySelect(category.id)}
              navigation={navigation}
            />
          ))}
        </ScrollView>

        <View style={styles.loyaltyPointsContainer}>
          {filteredLoyaltyPoints.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                {selectedCategory === 'all' ? 'No Loyalty Points Yet' : `No ${selectedCategoryData?.name} Programs`}
              </Text>
              <Text style={styles.emptySubtitle}>
                {selectedCategory === 'all' 
                  ? 'Start by adding your first loyalty program to keep it safe and organized'
                  : `Add your first ${selectedCategoryData?.name.toLowerCase()} program`
                }
              </Text>
              <TouchableOpacity style={styles.mainAddButton} onPress={handleAddLoyaltyPoint}>
                <Text style={styles.addButtonText}>
                  {selectedCategory === 'all' ? 'Add Your First Program' : `Add ${selectedCategoryData?.name} Program`}
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            filteredLoyaltyPoints.map((loyaltyPoint) => (
              <LoyaltyPointItem key={loyaltyPoint.id} item={loyaltyPoint} onPress={() => handleLoyaltyPointPress(loyaltyPoint)} />
            ))
          )}
        </View>
      </ScrollView>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#1C2C4F',
    fontSize: ms(16, 0.3),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  searchContainer: {
    margin: ms(20),
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
  },
  searchInput: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  searchIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allLoyaltyPointsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: ms(25),
    paddingBottom: ms(10),
  },
  loyaltyPointsContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  categoryCard: {
    width: s(130),
    height: s(160),
    borderRadius: ms(15),
    padding: ms(15),
    marginRight: ms(15),
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryCardContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%',
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(8),
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: ms(4),
  },
  categoryCount: {
    fontSize: ms(12, 0.3),
    textAlign: 'center',
    marginBottom: ms(5),
  },
  addButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    width: s(15),
    height: s(15),
  },
  emptyState: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
    paddingTop: ms(40),
    paddingBottom: ms(20),
  },
  emptyIcon: {
    width: s(80),
    height: s(80),
    opacity: 0.5,
    marginBottom: ms(20),
  },
  emptyTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(30),
  },
  loyaltyPointItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  loyaltyPointIcon: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loyaltyPointIconImage: {
    width: s(25),
    height: s(25),
    tintColor: '#FFFFFF',
  },
  loyaltyPointInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  loyaltyPointName: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    fontWeight: '500',
    marginBottom: ms(5),
  },
  loyaltyPointUrl: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  loyaltyPointType: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    minHeight: s(50),
  },
  loyaltyPointAccountTop: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  moreButton: {
    padding: ms(5),
  },
  moreButtonText: {
    fontSize: ms(20, 0.3),
    color: '#6B7280',
    letterSpacing: ms(2),
  },
  addButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  mainAddButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(20),
  },
});